/* eslint-disable no-param-reassign */
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import {
  LS_KEY_SHOW_COMPETITION_TIMELINE_MOBILE,
  LS_KEY_IS_COMPETITIONS_AUDIO_ENABLED,
} from 'src/constants/localStorageKeys';
import { CompetitionCreationScreens } from 'src/constants/competition';
import type {
  AttemptQuestionCompetitionResponse,
  GetCompetitionQuestionsResponse,
  GetCompetitionQuestionsSub,
} from 'src/openapi';
import {
  getAllCompetitions,
  registerInCompetition,
  fetchCompetitionAttemptHistory,
  getCompetitionQuestions,
  getCompetitionRegistrations,
  getCandidatesAnsweredQuestionsAndAnalytics,
  deleteAssociatedQuestionById,
  updateCompetitionDetails,
} from 'src/redux/actions/competition';
import { applyAiEnhanceQuestion } from 'src/redux/actions/employerLayout';
import type {
  CompetitionInitialState,
  AttemptHistoryWithQuestionNo,
} from '../types/competition';
import { createQuestion } from '../actions/contribute';

// Defined in src/constants, which keys REQUIRED_FIELDS_COMPETITION by it, and
// re-exported here so the modules importing it from this slice still resolve.
export { CompetitionCreationScreens };
const initialState: CompetitionInitialState = {
  allCompetitions: null,
  myCompetitions: null,
  timelineData: null,
  isFetchingAttemptHistory: false,
  showCompetitionTimelineMobile: JSON.parse(
    localStorage.getItem(LS_KEY_SHOW_COMPETITION_TIMELINE_MOBILE) ?? 'true',
  ),
  competitionSettings: {
    [LS_KEY_IS_COMPETITIONS_AUDIO_ENABLED]: JSON.parse(
      localStorage.getItem(LS_KEY_IS_COMPETITIONS_AUDIO_ENABLED) ?? 'true',
    ),
  },
  userCompetitionCreationScreen: CompetitionCreationScreens.BasicDetails,
  competitionCreationQuestions: null,
  competitionCreationQuestionsLoading: false,
  registeredList: null,
  attemptedQuestion: null,
  userCompetitionAnalytics: null,
};

const competitionSlice = createSlice({
  name: 'competition',
  initialState,
  reducers: {
    setCurrentUserCompetitionCreationScreen: (
      state,
      action: PayloadAction<CompetitionCreationScreens>,
    ) => {
      state.userCompetitionCreationScreen = action.payload;
    },
    resetTimeline: (state) => {
      state.timelineData = null;
    },
    updateTimelineOnAttempt: (
      state,
      action: PayloadAction<{
        attemptData: AttemptQuestionCompetitionResponse;
        attemptNo: number;
      }>,
    ) => {
      if (!state.timelineData?.list) {
        return;
      }

      state.timelineData.list.push({
        attempt_no: action.payload.attemptNo,
        created_at: action.payload.attemptData?.created_at,
        is_attempt_correct:
          action.payload.attemptData?.is_attempt_correct ?? undefined,
        question_id: action.payload.attemptData.question_id,
        question_skipped: action.payload.attemptData.question_skipped,
        timeout: action.payload.attemptData.timeout,
        id: action.payload.attemptData.attempt_id,
        is_attempt_complete: true,
      });
    },
    toggleShowCompetitionTimelineMobile: (state) => {
      const newValue = !state.showCompetitionTimelineMobile;
      state.showCompetitionTimelineMobile = newValue;
      localStorage.setItem(
        LS_KEY_SHOW_COMPETITION_TIMELINE_MOBILE,
        JSON.stringify(newValue),
      );
    },
    resetCompetitionState: () => initialState,
    changeCompetitionSetting: (
      state,
      action: PayloadAction<{ key: string; value: boolean }>,
    ) => {
      const { key, value } = action.payload;
      localStorage.setItem(key, JSON.stringify(value));
      state.competitionSettings = {
        ...state.competitionSettings,
        [key]: value,
      };
    },
    updateCompetitionQuestions: (
      state,
      action: PayloadAction<GetCompetitionQuestionsResponse>,
    ) => {
      state.competitionCreationQuestions = action.payload;
    },
    // Generated questions arrive one at a time, often a few ms apart, so they are
    // appended to the current list here rather than to a copy a handler captured.
    appendCompetitionQuestions: (
      state,
      action: PayloadAction<GetCompetitionQuestionsSub[]>,
    ) => {
      const current = state.competitionCreationQuestions ?? {};
      const known = new Set((current.questions ?? []).map((q) => q.id));
      state.competitionCreationQuestions = {
        ...current,
        questions: [
          ...(current.questions ?? []),
          ...action.payload.filter((q) => !known.has(q.id)),
        ],
      };
    },
    swapCompetitionQuestion: (
      state,
      action: PayloadAction<{
        oldId: string;
        question: GetCompetitionQuestionsSub;
      }>,
    ) => {
      const current = state.competitionCreationQuestions;
      if (!current?.questions) {
        return;
      }
      current.questions = current.questions.map((q) =>
        q.id === action.payload.oldId ? action.payload.question : q,
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllCompetitions.fulfilled, (state, action) => {
      // The public list scrolls infinitely and appends; the admin list pages
      // discretely and passes replace, or page 2 would stack under page 1.
      const replace = action.meta.arg.replace === true;
      if (action.meta.arg.isRegistered) {
        if (replace || action.payload.current_page === 1) {
          state.myCompetitions = action.payload;
        } else if (state.myCompetitions?.data) {
          state.myCompetitions.data = [
            ...state.myCompetitions.data,
            ...(action.payload.data ?? []),
          ];
          state.myCompetitions.current_page = action.payload.current_page;
          state.myCompetitions.total_pages = action.payload.total_pages;
          state.myCompetitions.page_size = action.payload.page_size;
        }
      } else if (!action.meta.arg.isRegistered) {
        if (replace || action.payload.current_page === 1) {
          state.allCompetitions = action.payload;
        } else if (state.allCompetitions?.data) {
          state.allCompetitions.data = [
            ...state.allCompetitions.data,
            ...(action.payload.data ?? []),
          ];
          state.allCompetitions.current_page = action.payload.current_page;
          state.allCompetitions.total_pages = action.payload.total_pages;
          state.allCompetitions.page_size = action.payload.page_size;
        }
      }
    });
    builder.addCase(registerInCompetition.fulfilled, (state) => {
      state.myCompetitions = null;
    });
    builder.addCase(getCompetitionQuestions.fulfilled, (state, action) => {
      state.competitionCreationQuestions = action.payload;
      state.competitionCreationQuestionsLoading = false;
    });
    builder.addCase(getCompetitionRegistrations.fulfilled, (state, action) => {
      state.registeredList = action.payload;
    });
    builder.addCase(
      getCandidatesAnsweredQuestionsAndAnalytics.fulfilled,
      (state, action) => {
        state.attemptedQuestion = action.payload.answered_questions || null;
        state.userCompetitionAnalytics = action.payload.analytics || null;
      },
    );
    builder.addCase(getCompetitionQuestions.pending, (state, action) => {
      if (!action.meta.arg.silent) {
        state.competitionCreationQuestionsLoading = true;
      }
    });
    builder.addCase(createQuestion.fulfilled, (state, action) => {
      state.competitionCreationQuestions = {
        questions: [
          ...(state.competitionCreationQuestions?.questions ?? []),
          {
            ...action.payload,
            created_by_name: 'Manual',
            title: action.payload.title ?? undefined,
            difficulty_level: action.payload.difficulty_level?.toString(),
            skill_array: action.payload.skill_array ?? undefined,
            solution_text: undefined,
            answer_one_word: action.payload.answer_one_word?.filter(
              (a): a is string => a !== null,
            ),
            answer_option_no: action.payload.answer_option_no?.filter(
              (a): a is number => a !== null,
            ),
          },
        ],
      };
    });
    builder.addCase(updateCompetitionDetails.fulfilled, (state, action) => {
      if (state.competitionCreationQuestions?.questions) {
        state.competitionCreationQuestions.questions =
          state.competitionCreationQuestions.questions.map((q) => {
            if (q.id === action.payload.id) {
              return action.payload as any;
            }
            return q;
          });
      }
    });
    builder
      .addCase(fetchCompetitionAttemptHistory.pending, (state) => {
        state.isFetchingAttemptHistory = true;
      })
      .addCase(fetchCompetitionAttemptHistory.fulfilled, (state, action) => {
        const newData: AttemptHistoryWithQuestionNo[] =
          action.payload.all_attempts?.map((attempt, idx) => ({
            ...attempt,
            attempt_no:
              ((action.payload.current_page ?? 1) - 1) *
                (action.payload.page_size ?? 0) +
              idx +
              1,
          })) ?? [];

        if (!state.timelineData) {
          state.timelineData = {
            list: newData,
            page_size: action.payload.page_size,
            current_page: action.payload.current_page,
            total_pages: action.payload.total_pages,
          };
        } else {
          state.timelineData.list = [...newData, ...state.timelineData.list];
          state.timelineData.page_size = action.payload.page_size;
          state.timelineData.current_page = action.payload.current_page;
          state.timelineData.total_pages = action.payload.total_pages;
        }
        state.isFetchingAttemptHistory = false;
      })
      .addCase(fetchCompetitionAttemptHistory.rejected, (state) => {
        state.isFetchingAttemptHistory = false;
      });
    builder.addCase(applyAiEnhanceQuestion.fulfilled, (state, action) => {
      const list = state.competitionCreationQuestions?.questions;
      if (!list) return;
      const { questionId, questionBody } = action.meta.arg;
      const idx = list.findIndex((q) => q.id === questionId);
      if (idx === -1) return;
      if (questionBody.question_text !== undefined)
        list[idx].question_text = questionBody.question_text;
      if (questionBody.options !== undefined)
        list[idx].options = questionBody.options;
      if (questionBody.answer_option_no !== undefined)
        list[idx].answer_option_no = questionBody.answer_option_no;
      if (questionBody.max_time !== undefined)
        list[idx].max_time = questionBody.max_time;
      if (questionBody.difficulty_level !== undefined)
        list[idx].difficulty_level = String(questionBody.difficulty_level);
    });
    builder.addCase(deleteAssociatedQuestionById.fulfilled, (state, action) => {
      const quesitonIds = action.meta.arg.questionBody.question_ids ?? [];
      const deleteAll = action.meta.arg.questionBody.delete_all ?? false;
      if (state.competitionCreationQuestions?.questions) {
        if (quesitonIds.length > 0) {
          state.competitionCreationQuestions.questions =
            state.competitionCreationQuestions.questions.filter(
              (q) => !quesitonIds.includes(q.id ?? ''),
            );
        }
        if (deleteAll) {
          state.competitionCreationQuestions.questions = [];
        }
      }
    });
  },
});
export const { setCurrentUserCompetitionCreationScreen } =
  competitionSlice.actions;

export const {
  resetTimeline,
  updateTimelineOnAttempt,
  toggleShowCompetitionTimelineMobile,
  resetCompetitionState,
  changeCompetitionSetting,
  updateCompetitionQuestions,
  appendCompetitionQuestions,
  swapCompetitionQuestion,
} = competitionSlice.actions;

export default competitionSlice.reducer;
